import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth, UserButton, ClerkLoading, ClerkFailed } from "@clerk/react";
import { Toaster } from "./components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import AppShell from "./components/layout/AppShell";
import { api, setTokenGetter, errMsg } from "./lib/api";
import { AppCtx } from "./lib/ctx";
import { Loader } from "./components/common/kit";
import Dashboard from "./pages/Dashboard";
import Crm from "./pages/Crm";
import { Customers, Customer360 } from "./pages/Customers";
import { Marketing, Design, SiteOps, Pmc, Maintenance, Documents, Tasks, Approvals } from "./pages/Simple";
import { Procurement, Finance } from "./pages/Ops";
import Estimation from "./pages/Estimation";
import { Quotations, QuotationDetail, Agreements, AgreementDetail } from "./pages/Commercial";
import { Packages, PackageQuotes, PackageQuoteDetail } from "./pages/Packages";
import { Projects, ProjectDetail } from "./pages/Projects";
import Handover from "./pages/Handover";
import Reports from "./pages/Reports";
import Ai from "./pages/Ai";
import SettingsPage from "./pages/Settings";
import Landing from "./pages/Landing";
import ClientPortal from "./pages/Portal";
import AdminPanel from "./pages/AdminPanel";
import Journey from "./pages/Journey";
import JourneyDetail from "./pages/JourneyDetail";
import PublicProposal from "./pages/PublicProposal";

const AuthLayout = ({ children, client }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-white">
    <div className="hidden lg:flex relative overflow-hidden">
      <img src="/brand/hero.jpg" alt="Modern home at dusk" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D131B]/90 via-[#0D131B]/40 to-[#0D131B]/20" />
      <div className="relative z-10 flex flex-col justify-between p-12 text-white">
        <img src="/brand/logo-full-dark.png" alt="ConstructONS" className="h-16 w-auto self-start" />
        <div>
          <p className="text-xs tracking-[0.3em] text-[#FF5A00] font-semibold mb-3">{client ? "CLIENT DASHBOARD" : "CONSTRUCTONS TEAM"}</p>
          <p className="text-4xl font-bold leading-tight">{client ? <>Your home,<br /><span className="grad-text">live and transparent.</span></> : <>Dream Homes<br /><span className="grad-text">Built Better.</span></>}</p>
          <p className="mt-3 text-neutral-300">{client ? "Sign in with the email address your ConstructONS project team invited." : "One connected platform — from first enquiry to long-term maintenance."}</p>
        </div>
      </div>
    </div>
    <div className="flex flex-col items-center justify-center p-6 gap-6">
      <img src="/brand/logo-full.png" alt="ConstructONS" className="h-12 w-auto lg:hidden" />
      <ClerkLoading><p className="text-sm text-neutral-500" data-testid="auth-loading">Loading sign-in...</p></ClerkLoading>
      {children}
      <ClerkFailed><div className="max-w-sm text-center" data-testid="auth-unavailable"><p className="font-semibold text-[#0D131B]">Sign-in is temporarily unavailable</p>
        <p className="text-sm text-neutral-500 mt-1">The sign-in service didn't respond. Please wait a minute and try again.</p>
        <button className="btn-grad mt-4" onClick={() => window.location.reload()} data-testid="auth-retry-btn">Try again</button></div></ClerkFailed>
      <a href="/" className="text-xs text-neutral-500 hover:text-[#FF5A00]" data-testid="back-home-link">← Back to home</a>
    </div>
  </div>
);

function Pending({ me }) {
  return (
    <AuthLayout>
      <div className="max-w-sm text-center" data-testid="pending-approval">
        <h1 className="text-2xl font-bold text-[#0D131B]">Awaiting approval</h1>
        <p className="text-sm text-neutral-500 mt-2">Signed in as {me?.email}. The company owner must assign you a role in Team & Settings before you can access ConstructONS.</p>
        <div className="mt-6 flex justify-center"><UserButton /></div>
      </div>
    </AuthLayout>
  );
}

function Guard({ m, me, children }) {
  return me.modules.includes(m) ? children : <div className="card-c p-10 text-center" data-testid="no-access">You do not have access to this module.</div>;
}

function Workspace() {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  setTokenGetter(getToken);
  const refresh = useCallback(async () => {
    try {
      const m = (await api.get("/me")).data;
      setMe(m);
      if (!["pending", "client"].includes(m.role)) {
        const [u, s] = await Promise.all([api.get("/users"), api.get("/settings")]);
        setUsers(u.data.users); setSettings(s.data);
      }
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  if (err) return <div className="p-10 text-center text-sm" data-testid="workspace-error">{err}</div>;
  if (!me) return <Loader label="Opening your workspace..." />;
  if (me.role === "client") return <ClientPortal />;
  if (me.role === "pending" || me.active === false) return <Pending me={me} />;
  const G = (m, el) => <Guard m={m} me={me}>{el}</Guard>;
  return (
    <AppCtx.Provider value={{ me, users, settings, refresh }}>
      <AppShell>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/marketing" element={G("marketing", <Marketing />)} />
          <Route path="/crm" element={G("crm", <Crm />)} />
          <Route path="/journey" element={G("crm", <Journey />)} />
          <Route path="/journey/:id" element={G("crm", <JourneyDetail />)} />
          <Route path="/customers" element={G("customers", <Customers />)} />
          <Route path="/customers/:id" element={G("customers", <Customer360 />)} />
          <Route path="/design" element={G("design", <Design />)} />
          <Route path="/estimation" element={G("estimation", <Estimation />)} />
          <Route path="/quotations" element={G("quotations", <Quotations />)} />
          <Route path="/quotations/:id" element={G("quotations", <QuotationDetail />)} />
          <Route path="/packages" element={G("quotations", <Packages />)} />
          <Route path="/package-quotes" element={G("quotations", <PackageQuotes />)} />
          <Route path="/package-quotes/:id" element={G("quotations", <PackageQuoteDetail />)} />
          <Route path="/agreements" element={G("agreements", <Agreements />)} />
          <Route path="/agreements/:id" element={G("agreements", <AgreementDetail />)} />
          <Route path="/projects" element={G("projects", <Projects />)} />
          <Route path="/projects/:id" element={G("projects", <ProjectDetail />)} />
          <Route path="/site" element={G("site", <SiteOps />)} />
          <Route path="/procurement" element={G("procurement", <Procurement />)} />
          <Route path="/finance" element={G("finance", <Finance />)} />
          <Route path="/pmc" element={G("pmc", <Pmc />)} />
          <Route path="/handover" element={G("handover", <Handover />)} />
          <Route path="/maintenance" element={G("maintenance", <Maintenance />)} />
          <Route path="/documents" element={G("documents", <Documents />)} />
          <Route path="/reports" element={G("reports", <Reports />)} />
          <Route path="/ai" element={G("ai", <Ai />)} />
          <Route path="/admin" element={G("settings", <AdminPanel />)} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </AppCtx.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/p/:token" element={<PublicProposal />} />
          <Route path="/sign-in/*" element={<AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>} />
          <Route path="/sign-up/*" element={<AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>} />
          <Route path="/client-login/*" element={<AuthLayout client><SignIn routing="path" path="/client-login" signUpUrl="/client-signup" withSignUp /></AuthLayout>} />
          <Route path="/client-signup/*" element={<AuthLayout client><SignUp routing="path" path="/client-signup" signInUrl="/client-login" /></AuthLayout>} />
          <Route path="/*" element={<>
            <Show when="signed-in"><Workspace /></Show>
            <Show when="signed-out"><Landing /></Show>
          </>} />
        </Routes>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
